import { useState } from 'react';
import { Loader2, AlertCircle, ArrowRight, X } from 'lucide-react';

interface PlanChangePreview {
  type: 'plan_change';
  current_plan: string;
  new_plan: string;
  new_plan_id: string;
  billing_interval: string;
  change_type: 'upgrade' | 'downgrade';
  new_plan_price: number;
  proration_amount: number;
  amount_due: number;
  credit_amount: number;
  charge_amount: number;
  next_billing_date: string;
  currency: string;
}

interface PackageAddPreview {
  type: 'package_add';
  package_id: string;
  package_name?: string;
  package_title?: string;
  package_description?: string;
  package_details?: string;
  monthly_price: number;
  proration_amount: number;
  amount_due_now: number;
  next_billing_date: string;
  currency: string;
}

type PreviewData = PlanChangePreview | PackageAddPreview;

interface SubscriptionPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  preview: PreviewData | null;
  isLoading?: boolean;
  error?: string | null;
  modalType?: 'plan_change' | 'package_add';
}

export function SubscriptionPreviewModal({
  isOpen,
  onClose,
  onConfirm,
  preview,
  isLoading = false,
  error = null,
  modalType,
}: SubscriptionPreviewModalProps) {
  const [confirming, setConfirming] = useState(false);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await onConfirm();
    } finally {
      setConfirming(false);
    }
  };

  const formatCurrency = (amount: number, currency = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Use modalType prop as fallback when preview is null (e.g., during error state)
  const isPlanChange = preview?.type === 'plan_change' || (!preview && modalType === 'plan_change');
  const isPackageAdd = preview?.type === 'package_add' || (!preview && modalType === 'package_add');

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={() => !confirming && !isLoading && onClose()}
    >
      <div 
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {isPlanChange ? 'Confirm Plan Change' : 'Confirm Package Addition'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            disabled={confirming || isLoading}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" />
            <span className="ml-3 text-gray-600 dark:text-gray-400">Calculating...</span>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
            <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
          </div>
        )}

        {preview && !isLoading && (
          <>
            {isPlanChange && (
              <div className="space-y-4">
                <div className="flex items-center justify-center gap-3 py-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <span className="font-medium text-gray-700 dark:text-gray-300 capitalize">
                    {(preview as PlanChangePreview).current_plan}
                  </span>
                  <ArrowRight className="w-5 h-5 text-gray-400" />
                  <span className="font-semibold text-[#1E3A8A] dark:text-[#60A5FA] capitalize">
                    {(preview as PlanChangePreview).new_plan}
                  </span>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">New plan price</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatCurrency((preview as PlanChangePreview).new_plan_price)}/{(preview as PlanChangePreview).billing_interval === 'yearly' ? 'year' : 'month'}
                    </span>
                  </div>

                  {(preview as PlanChangePreview).charge_amount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Prorated charge (due now)</span>
                      <span className="font-medium text-amber-600 dark:text-amber-400">
                        {formatCurrency((preview as PlanChangePreview).charge_amount)}
                      </span>
                    </div>
                  )}

                  {(preview as PlanChangePreview).credit_amount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Prorated credit</span>
                      <span className="font-medium text-green-600 dark:text-green-400">
                        -{formatCurrency((preview as PlanChangePreview).credit_amount)}
                      </span>
                    </div>
                  )}

                  <div className="pt-2 border-t border-gray-200 dark:border-gray-600 flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Next billing date</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatDate((preview as PlanChangePreview).next_billing_date)}
                    </span>
                  </div>
                </div>

                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {(preview as PlanChangePreview).change_type === 'upgrade' 
                    ? 'Your plan will be upgraded immediately. You will be charged the prorated difference.'
                    : 'Your plan will be downgraded immediately. Any credit will be applied to your next invoice.'}
                </p>
              </div>
            )}

            {isPackageAdd && (
              <div className="space-y-4">
                <div className="py-3 px-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {(preview as PackageAddPreview).package_title ?? (preview as PackageAddPreview).package_name}
                  </p>
                  {((preview as PackageAddPreview).package_details ?? (preview as PackageAddPreview).package_description) && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {(preview as PackageAddPreview).package_details ?? (preview as PackageAddPreview).package_description}
                    </p>
                  )}
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Monthly price</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatCurrency((preview as PackageAddPreview).monthly_price)}/month
                    </span>
                  </div>

                  {(preview as PackageAddPreview).amount_due_now > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Prorated charge (due now)</span>
                      <span className="font-medium text-amber-600 dark:text-amber-400">
                        {formatCurrency((preview as PackageAddPreview).amount_due_now)}
                      </span>
                    </div>
                  )}

                  <div className="pt-2 border-t border-gray-200 dark:border-gray-600 flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Next billing date</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatDate((preview as PackageAddPreview).next_billing_date)}
                    </span>
                  </div>
                </div>

                <p className="text-xs text-gray-500 dark:text-gray-400">
                  This package will be added to your subscription immediately. You will be charged the prorated amount for the remainder of your billing period.
                </p>
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                disabled={confirming}
                className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={confirming}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white font-medium disabled:opacity-50"
              >
                {confirming && <Loader2 className="w-4 h-4 animate-spin" />}
                {isPlanChange 
                  ? ((preview as PlanChangePreview).change_type === 'upgrade' ? 'Confirm Upgrade' : 'Confirm Downgrade')
                  : 'Add Package'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
